import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Coffee,
  Utensils,
  Waves,
  Landmark,
  MapPin,
  ArrowRight,
  Star,
  Building,
  Croissant,
  Store,
  Pyramid } from
'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useSeoMeta, SITE_URL } from '../hooks/useSeoMeta';
import { useHeroImages } from '../hooks/useHeroImages';
import { HeroSlideshow } from '../components/HeroSlideshow';
import { DIFFICULTY_COLORS, ITINERARIES } from '../data/itineraries';
import { TripPlannerPromo } from '../components/TripPlannerPromo';
import { StreetFoodSection } from '../components/StreetFoodSection';
import type { CatalogPlace } from '../data/places-catalog';
import { CATEGORIES as SCHEMA_CATEGORIES } from '../data/places-catalog';
import type { NeighborhoodDto } from '../lib/api-types';
import {
  getPlaces,
  getPlacesAllPages,
  getNeighborhoodsList,
  getItineraries,
} from '../lib/api';
const HOME_CATEGORY_QUICK_LINKS = [
{
  id: 'restaurants',
  icon: Utensils,
  en: 'Restaurants',
  ar: 'مطاعم'
},
{
  id: 'cafes',
  icon: Coffee,
  en: 'Cafes',
  ar: 'كافيهات'
},
{
  id: 'beaches',
  icon: Waves,
  en: 'Beaches',
  ar: 'شواطئ'
},
{
  id: 'attractions',
  icon: Landmark,
  en: 'Attractions',
  ar: 'معالم سياحية'
},
{
  id: 'museums',
  icon: Building,
  en: 'Museums',
  ar: 'متاحف'
},
{
  id: 'bakeries',
  icon: Croissant,
  en: 'Bakeries',
  ar: 'مخابز وحلواني'
},
{
  id: 'markets',
  icon: Store,
  en: 'Markets',
  ar: 'أسواق'
},
{
  id: 'heritage',
  icon: Pyramid,
  en: 'Heritage',
  ar: 'مواقع تراثية'
}];

type FeaturedGem = {
  id: string;
  name: {
    en: string;
    ar: string;
  };
  neighborhood: {
    en: string;
    ar: string;
  };
  category: {
    en: string;
    ar: string;
  };
  rating: number;
  image: string;
};

type HoodPreview = {
  slug: string;
  name: {
    en: string;
    ar: string;
  };
  count: number;
  image: string;
};

function catalogPlaceToFeaturedGem(p: CatalogPlace): FeaturedGem {
  const catSchema = SCHEMA_CATEGORIES.find((c) => c.slug === p.category);
  return {
    id: p.id,
    name: p.name,
    neighborhood: p.neighborhood,
    category:
      p.categoryLabel ?? {
        en: catSchema?.en ?? p.category,
        ar: catSchema?.ar ?? p.category,
      },
    rating: p.rating,
    image: p.coverImage,
  };
}

function buildNeighborhoodPreview(
  list: NeighborhoodDto[],
  places: CatalogPlace[],
): HoodPreview[] {
  const bySlug: Record<string, number> = {};
  const byName: Record<string, number> = {};
  for (const p of places) {
    byName[p.neighborhood.en] = (byName[p.neighborhood.en] ?? 0) + 1;
    if (p.neighborhoodSlug) {
      bySlug[p.neighborhoodSlug] =
        (bySlug[p.neighborhoodSlug] ?? 0) + 1;
    }
  }
  return list.slice(0, 5).map((n) => ({
    slug: n.slug,
    name: n.name,
    count: bySlug[n.slug] ?? byName[n.name.en] ?? 0,
    image: n.heroImage,
  }));
}

const FEATURED_GEMS_FALLBACK: FeaturedGem[] = [
{
  id: 'sea-breeze-cafe',
  name: {
    en: 'Sea Breeze Café',
    ar: 'مقهى نسيم البحر'
  },
  neighborhood: {
    en: 'Stanley',
    ar: 'ستانلي'
  },
  category: {
    en: 'Cafe',
    ar: 'كافيه'
  },
  rating: 4.8,
  image:
  'https://images.unsplash.com/photo-1559925393-8be0ec4767c8?q=80&w=800&auto=format&fit=crop'
},
{
  id: 'corniche-bites',
  name: {
    en: 'Corniche Bites',
    ar: 'لقم الكورنيش'
  },
  neighborhood: {
    en: 'San Stefano',
    ar: 'سان ستيفانو'
  },
  category: {
    en: 'Restaurant',
    ar: 'مطعم'
  },
  rating: 4.7,
  image:
  'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=800&auto=format&fit=crop'
},
{
  id: 'vintage-alex',
  name: {
    en: 'Vintage Alex',
    ar: 'إسكندرية زمان'
  },
  neighborhood: {
    en: 'Mansheya',
    ar: 'المنشية'
  },
  category: {
    en: 'Museum',
    ar: 'متحف'
  },
  rating: 4.9,
  image:
  'https://images.unsplash.com/photo-1525648199074-cee30ba79a4a?q=80&w=800&auto=format&fit=crop'
}];

const NEIGHBORHOODS_PREVIEW_FALLBACK: HoodPreview[] = [
{
  slug: 'stanley',
  name: {
    en: 'Stanley',
    ar: 'ستانلي'
  },
  count: 4,
  image:
  'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=800&auto=format&fit=crop'
},
{
  slug: 'mansheya',
  name: {
    en: 'Mansheya',
    ar: 'المنشية'
  },
  count: 8,
  image:
  'https://images.unsplash.com/photo-1525648199074-cee30ba79a4a?q=80&w=800&auto=format&fit=crop'
},
{
  slug: 'gleem',
  name: {
    en: 'Gleem',
    ar: 'جليم'
  },
  count: 5,
  image:
  'https://images.unsplash.com/photo-1497935586351-b67a49e012bf?q=80&w=800&auto=format&fit=crop'
},
{
  slug: 'anfoushi',
  name: {
    en: 'Anfoushi',
    ar: 'الأنفوشي'
  },
  count: 6,
  image:
  'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=800&auto=format&fit=crop'
},
{
  slug: 'montaza',
  name: {
    en: 'Montaza',
    ar: 'المنتزه'
  },
  count: 3,
  image:
  'https://images.unsplash.com/photo-1600623471616-8c1966c91ff6?q=80&w=800&auto=format&fit=crop'
}];

export function HomePage() {
  const { t, ui, isRTL, lang } = useLanguage();
  const isAr = lang === 'ar';
  const webSiteJsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Alex Hidden Gems',
      url: `${SITE_URL}/`,
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${SITE_URL}/places?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    }),
    [],
  );
  useSeoMeta({
    title: lang === 'ar' ? 'الجواهر الخفية للإسكندرية' : null,
    description: isAr
      ? 'اكتشف روح الإسكندرية — أماكن وأحياء ومسارات وقصص محلية منتقاة من ساحل البحر المتوسط.'
      : "Discover the Soul of Alexandria — curated places, neighborhoods, itineraries, and local stories from Egypt's Mediterranean coast.",
    path: '/',
    lang,
    ogType: 'website',
    jsonLd: webSiteJsonLd,
  });
  const heroImages = useHeroImages();

  const [featuredGems, setFeaturedGems] =
    useState<FeaturedGem[]>(FEATURED_GEMS_FALLBACK);
  const [hoodPreview, setHoodPreview] = useState<HoodPreview[]>(
    NEIGHBORHOODS_PREVIEW_FALLBACK,
  );
  type ItinBundle = (typeof ITINERARIES)[number];
  const [homeItineraries, setHomeItineraries] = useState<ItinBundle[]>(
    ITINERARIES.slice(0, 4),
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settled = await Promise.allSettled([
          getPlaces({ limit: 100, editorsPicks: true }),
          Promise.all([
            getNeighborhoodsList({ limit: 200 }),
            getPlacesAllPages(),
          ]),
          getItineraries({ limit: 4 }),
        ]);

        if (settled[0].status === 'fulfilled') {
          const gems = settled[0].value.data
            .slice(0, 3)
            .map(catalogPlaceToFeaturedGem);
          if (!cancelled && gems.length > 0) setFeaturedGems(gems);
        } else {
          console.warn(
            '[api] places fetch failed, using bundled fallback',
          );
        }

        if (settled[1].status === 'fulfilled') {
          const [nhRes, plRows] = settled[1].value;
          const carousel = buildNeighborhoodPreview(
            nhRes.data as NeighborhoodDto[],
            plRows,
          );
          if (!cancelled && carousel.length > 0) setHoodPreview(carousel);
        } else {
          console.warn(
            '[api] neighborhoods fetch failed, using bundled fallback',
          );
        }

        if (settled[2].status === 'fulfilled') {
          const rows = settled[2].value.data as ItinBundle[];
          if (!cancelled && rows?.length > 0)
            setHomeItineraries(rows.slice(0, 4));
        } else {
          console.warn(
            '[api] itineraries fetch failed, using bundled fallback',
          );
        }
      } catch {
        /* errors logged per settled branch */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="flex-grow">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <HeroSlideshow images={heroImages} />

        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center mt-16">
          <motion.div
            initial={{
              opacity: 0,
              y: 20
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.8
            }}>
            
            <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl text-white font-bold mb-6 md:mb-10 drop-shadow-lg leading-tight">
              {ui.heroTitle}
            </h1>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{
              opacity: 0,
              y: 20
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.8,
              delay: 0.2
            }}
            className="bg-white p-1.5 sm:p-2 rounded-2xl shadow-float flex items-center max-w-2xl mx-auto">
            
            <div className="px-3 sm:px-4 text-sea-blue/85 shrink-0">
              <Search size={20} className="sm:hidden" />
              <Search size={24} className="hidden sm:block" />
            </div>
            <input
              type="text"
              placeholder={ui.heroSearchPlaceholder}
              className="flex-grow bg-transparent border-none outline-none py-2.5 sm:py-3 px-1 sm:px-2 text-sea-blue placeholder:text-sea-blue/55 font-medium text-sm sm:text-base min-w-0" />
            
            <button
              className="bg-terracotta hover:bg-terracotta-hover text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium transition-colors shrink-0 flex items-center gap-2"
              aria-label={ui.search}>
              
              <Search size={18} className="sm:hidden" />
              <span className="hidden sm:inline">{ui.search}</span>
            </button>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 md:py-16 space-y-14 sm:space-y-20 md:space-y-24">
        {/* Categories */}
        <section>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-sea-blue font-bold flex items-center gap-3">
                <Coffee className="text-cyan-700" size={28} />
                {lang === 'ar' ?
                'حاجات تعملها في إسكندرية' :
                'Things to Do in Alexandria'}
              </h2>
              <p className="text-sea-blue/85 mt-2">
                {lang === 'ar' ?
                'من المقاهي للشواطئ، من الآثار للأسواق' :
                'From cafes to beaches, monuments to markets'}
              </p>
            </div>
            <Link
              to="/categories"
              className="flex items-center gap-2 text-cyan-700 font-medium hover:text-cyan-700 transition-colors">
              
              {lang === 'ar' ? 'كل الحاجات' : 'View all'}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 sm:gap-4 lg:gap-5">
            {HOME_CATEGORY_QUICK_LINKS.map((cat) =>
            <Link
              key={cat.id}
              to={`/places?category=${cat.id}`}
              className="group bg-white rounded-2xl p-4 lg:p-5 flex flex-col items-center justify-center gap-3 shadow-soft hover:shadow-float transition-all border border-sea-blue/5 hover:border-cyan-500/30">
              
                <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-sand flex items-center justify-center text-sea-blue group-hover:bg-sea-blue group-hover:text-white transition-colors">
                  <cat.icon size={22} />
                </div>
                <div className="text-center">
                  <div className="font-serif font-bold text-sea-blue text-sm lg:text-base leading-tight">
                    {t(cat)}
                  </div>
                </div>
              </Link>
            )}
          </div>
        </section>

        {/* Street Food Section */}
        <StreetFoodSection />

        {/* Featured Gems */}
        <section>
          <div className="flex flex-col md:flex-row justify-between items-end mb-8 gap-4">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-sea-blue font-bold flex items-center gap-3">
                <Star className="text-terracotta fill-terracotta" size={28} />
                {ui.editorsPicks}
              </h2>
              <p className="text-sea-blue/85 mt-2">{ui.editorsPicksSub}</p>
            </div>
            <Link
              to="/places"
              className="flex items-center gap-2 text-terracotta font-medium hover:text-terracotta-hover transition-colors">
              
              {ui.viewAll}{' '}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredGems.map((place) =>
            <Link
              key={place.id}
              to={`/places/${place.id}`}
              className="group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-float transition-all duration-300 border border-sea-blue/5 flex flex-col">
              
                <div className="relative h-56 overflow-hidden">
                  <img
                  src={place.image}
                  alt={t(place.name)}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                
                  <div className="absolute top-3 end-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg flex items-center gap-1 text-sm font-bold text-sea-blue">
                    <Star
                    size={14}
                    className="fill-terracotta text-terracotta" />
                  
                    <span className="ltr-numerals">{place.rating}</span>
                  </div>
                </div>

                <div className="p-6 flex-grow flex flex-col justify-between">
                  <div>
                    <div className="text-xs font-bold tracking-wider text-cyan-700 uppercase mb-2">
                      {t(place.category)}
                    </div>
                    <h3 className="font-serif text-2xl font-bold text-sea-blue group-hover:text-terracotta transition-colors">
                      {t(place.name)}
                    </h3>
                  </div>

                  <div className="flex items-center gap-1.5 text-sm text-sea-blue/75 mt-4 pt-4 border-t border-sea-blue/5">
                    <MapPin size={16} className="text-terracotta" />
                    <span>{t(place.neighborhood)}</span>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </section>

        {/* Explore by Neighborhood */}
        <section>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-sea-blue font-bold flex items-center gap-3">
                <Landmark className="text-sea-blue-light" size={28} />
                {ui.wanderNeighborhoods}
              </h2>
              <p className="text-sea-blue/85 mt-2">
                {lang === 'ar' ?
                'كل حي له طابع وقصة' :
                'Every neighborhood tells its own story'}
              </p>
            </div>
            <Link
              to="/neighborhoods"
              className="flex items-center gap-2 text-sea-blue-light font-medium hover:text-sea-blue transition-colors">
              
              {lang === 'ar' ? 'كل الأحياء' : 'View all neighborhoods'}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </Link>
          </div>

          <div className="flex overflow-x-auto pb-6 -mx-4 px-4 sm:mx-0 sm:px-0 gap-4 hide-scrollbar snap-x">
            {hoodPreview.map((hood) =>
            <Link
              key={hood.slug}
              to={`/neighborhoods/${hood.slug}`}
              className="group relative w-64 sm:w-72 shrink-0 h-80 rounded-2xl overflow-hidden shadow-soft hover:shadow-float transition-all snap-start">
              
                <img
                src={hood.image}
                alt={t(hood.name)}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-sea-blue/90 via-sea-blue/20 to-transparent" />

                <div className="absolute bottom-0 start-0 w-full p-6 text-white">
                  <div className="bg-white/20 backdrop-blur-sm w-fit px-2 py-1 rounded text-xs font-bold mb-2">
                    <span className="ltr-numerals">{hood.count}</span>{' '}
                    {ui.placesCount}
                  </div>
                  <h3 className="font-serif text-2xl font-bold group-hover:text-sand transition-colors">
                    {t(hood.name)}
                  </h3>
                </div>
              </Link>
            )}
          </div>
        </section>

        {/* Trip Planner Promo */}
        <TripPlannerPromo />

        {/* Curated Itineraries */}
        <section>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-sea-blue font-bold flex items-center gap-3">
                <MapPin className="text-cyan-700" size={28} />
                {ui.curatedItineraries}
              </h2>
              <p className="text-sea-blue/85 mt-2">
                {lang === 'ar' ?
                'مسارات نصف يوم، طرق غروب الشمس، وخطط لويكند كامل' :
                'Half-day strolls, sunset routes, and full weekend plans'}
              </p>
            </div>
            <Link
              to="/itineraries"
              className="flex items-center gap-2 text-cyan-700 font-medium hover:text-cyan-700 transition-colors">
              
              {ui.allItineraries}{' '}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {homeItineraries.map((itinerary) =>
            <Link
              key={itinerary.id}
              to={`/itinerary/${itinerary.id}`}
              className="group relative h-80 rounded-2xl overflow-hidden shadow-soft hover:shadow-float transition-all border border-sea-blue/5">
              
                <img
                src={itinerary.coverImage}
                alt={t(itinerary.name)}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-sea-blue/90 via-sea-blue/40 to-transparent" />

                {/* Badges */}
                <div className="absolute top-4 start-4 flex flex-col items-start gap-2 z-10">
                  <div
                  className={`bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-md text-xs font-bold text-sea-blue shadow-sm ${isAr ? 'font-arabic' : ''}`}>
                  
                    {t(itinerary.duration)}
                  </div>
                  <div
                  className={`bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-md text-xs font-bold text-sea-blue shadow-sm ${isAr ? 'font-arabic' : ''}`}>
                  
                    <span className="ltr-numerals">
                      {itinerary.steps.length}
                    </span>{' '}
                    {isAr ? 'محطات' : 'stops'}
                  </div>
                  <div
                  className={`px-2.5 py-1 rounded-md text-xs font-bold shadow-sm ${DIFFICULTY_COLORS[itinerary.difficulty]} ${isAr ? 'font-arabic' : ''}`}>
                  
                    {itinerary.difficulty}
                  </div>
                </div>

                <div className="absolute bottom-0 start-0 w-full p-6 sm:p-8">
                  <div className="text-white">
                    <h3
                    className={`font-serif text-3xl font-bold mb-2 ${isAr ? 'font-arabic' : ''}`}>
                    
                      {t(itinerary.name)}
                    </h3>
                    <p
                    className={`text-white/80 text-sm md:text-base max-w-md line-clamp-2 ${isAr ? 'font-arabic' : ''}`}>
                    
                      {t(itinerary.description)}
                    </p>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </section>
      </div>
    </main>);

}